import React, { useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  AddRounded,
  ApartmentOutlined,
  Close,
  CloudUploadOutlined,
  NotesOutlined,
  PaymentsOutlined,
  PersonOutlined,
  PhotoLibraryOutlined,
  PlaceOutlined,
} from "@mui/icons-material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { styled } from "@mui/material/styles";
import SelectedPhotos from "../components/SelectedPhotos";
import FormSection from "../components/FormSection";
import { dayString, startOfToday } from "../utils/dates";
import { radius, tokens } from "../theme/theme";

const HiddenInput = styled("input")({ display: "none" });

/** Every field the save button insists on. */
const REQUIRED = ["street", "region", "rooms", "floor", "cost", "heating"];

const AddRealEstateObject = ({
  onAddItem,
  onCloseDialog,
  regionSearchTerms,
  streetSearchTerms,
}) => {
  const inputFile = useRef(null);
  const [inputFiles, setInputFiles] = useState([]);
  const [personName, setPersonName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [dateFrom, setDateFrom] = useState(dayString(new Date()));
  const [rooms, setRooms] = useState(1);
  const [cost, setCost] = useState(6600);
  const [currency, setCurrency] = useState("");
  const [street, setStreet] = useState("");
  const [region, setRegion] = useState("");
  const [floor, setFloor] = useState(1);
  const [state, setState] = useState("");
  const [heating, setHeating] = useState("");
  const [info, setInfo] = useState("");
  const [furniture, setFurniture] = useState(false);
  const [technology, setTechnology] = useState(false);
  const [displayDateFrom, setDisplayDateFrom] = useState(new Date());

  // Shown only after a failed save attempt. The old dialog's save button did
  // nothing at all when a required field was empty — no message, no highlight —
  // which read as a broken button.
  const [showErrors, setShowErrors] = useState(false);

  const values = {
    street: street,
    region: region,
    rooms: rooms,
    floor: floor,
    cost: cost,
    heating: heating,
  };
  const missing = REQUIRED.filter(function (key) {
    return values[key] === "" || values[key] === null;
  });

  const handleFileInput = (e) => {
    var selectedFiles = [];
    if (!e.target.files || e.target.files.length === 0) {
      return;
    }
    for (var i = 0; i < e.target.files.length; i++) {
      selectedFiles.push(e.target.files[i]);
    }
    setInputFiles([...inputFiles, ...selectedFiles]);
    // Let the same file be picked again after removing it.
    e.target.value = "";
  };

  const removeFile = (fileVar) => {
    setInputFiles(inputFiles.filter((file) => file !== fileVar));
  };

  const onSave = (e) => {
    e.preventDefault();
    onAddItem(
      {
        id: `${Math.floor(Math.random() * 1000000) +
          1}-${new Date().getTime()}`,
        personName: personName,
        phoneNumber: phoneNumber,
        dateFrom: new Date(dateFrom).getTime(),
        rooms: rooms,
        cost: cost,
        currency: currency,
        street: street,
        region: region,
        floor: floor,
        state: state,
        heating: heating,
        info: info,
        furniture: furniture,
        technology: technology,
        dateEdit: startOfToday(),
        // The order the photos were arranged in below; the first is the cover.
        // By name, because that is what uploadPhotos keys each file by.
        photoOrder: inputFiles.map((file) => file.name),
      },
      inputFiles
    );
  };

  const trySave = (e) => {
    if (missing.length === 0) {
      onSave(e);
    } else {
      setShowErrors(true);
    }
  };

  // Enter should not submit a half-filled form from any single field.
  const noEnter = (e) => {
    if (e.key === "Enter") e.preventDefault();
  };

  const required = (value, label) => ({
    error: showErrors && value === "",
    helperText: showErrors && value === "" ? label || "Заповніть це поле" : " ",
  });

  return (
    <Dialog
      open={true}
      fullWidth
      maxWidth="md"
      scroll="paper"
      onClose={onCloseDialog}
      PaperProps={{ sx: { borderRadius: radius.lg } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          borderBottom: "1px solid " + tokens.line,
          py: 2,
        }}
      >
        <ApartmentOutlined sx={{ color: tokens.blue600 }} />
        <Box sx={{ flexGrow: 1, lineHeight: 1.2 }}>
          <Typography variant="h6" component="div" sx={{ lineHeight: 1.2 }}>
            Нове оголошення
          </Typography>
          <Typography variant="caption" sx={{ color: tokens.ink400 }}>
            Поля з зірочкою обов'язкові
          </Typography>
        </Box>
        <IconButton onClick={onCloseDialog} aria-label="Закрити">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ backgroundColor: tokens.canvas }}>
        <Box component="form" noValidate autoComplete="off" sx={{ pt: 3 }}>
          <FormSection title="Адреса" icon={<PlaceOutlined fontSize="small" />}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={8}>
                <Autocomplete
                  freeSolo
                  options={streetSearchTerms.map((option) => option)}
                  onInputChange={(e, newValue) => setStreet(newValue)}
                  onKeyPress={noEnter}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      type="text"
                      variant="outlined"
                      label="Вулиця *"
                      {...required(street)}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  freeSolo
                  options={regionSearchTerms.map((option) => option)}
                  onInputChange={(e, newValue) => setRegion(newValue)}
                  onKeyPress={noEnter}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      type="text"
                      variant="outlined"
                      label="Район *"
                      {...required(region)}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </FormSection>

          <FormSection title="Ціна" icon={<PaymentsOutlined fontSize="small" />}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  id="add-cost"
                  label="Ціна *"
                  variant="outlined"
                  type="number"
                  value={cost}
                  onKeyPress={noEnter}
                  onChange={(e) => setCost(e.target.value)}
                  fullWidth
                  {...required(cost)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  freeSolo
                  options={["грн", "$"]}
                  onInputChange={(e, newValue) => setCurrency(newValue)}
                  onKeyPress={noEnter}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      type="text"
                      variant="outlined"
                      label="Валюта"
                      helperText=" "
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    inputFormat="dd.MM.yyyy"
                    mask="__.__.____"
                    value={displayDateFrom}
                    onChange={(newValue) => {
                      setDisplayDateFrom(newValue);
                      if (newValue === null) setDateFrom("");
                      else
                        setDateFrom(dayString(newValue));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        inputProps={{
                          ...params.inputProps,
                          placeholder: "дд.мм.рррр",
                        }}
                        onKeyPress={noEnter}
                        fullWidth
                        variant="outlined"
                        label="Дата заповнення"
                        helperText=" "
                      />
                    )}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>
          </FormSection>

          <FormSection
            title="Характеристики"
            icon={<ApartmentOutlined fontSize="small" />}
          >
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <TextField
                  id="add-rooms"
                  label="Кімнат *"
                  variant="outlined"
                  type="number"
                  value={rooms}
                  onKeyPress={noEnter}
                  onChange={(e) => setRooms(e.target.value)}
                  fullWidth
                  {...required(rooms)}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  id="add-floor"
                  label="Поверх *"
                  variant="outlined"
                  type="number"
                  value={floor}
                  onKeyPress={noEnter}
                  onChange={(e) => setFloor(e.target.value)}
                  fullWidth
                  {...required(floor)}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Autocomplete
                  freeSolo
                  options={["Санітарний", "Під Євро", "Євро"]}
                  onInputChange={(e, newValue) => setState(newValue)}
                  onKeyPress={noEnter}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      type="text"
                      variant="outlined"
                      label="Стан"
                      helperText=" "
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Autocomplete
                  freeSolo
                  options={["Центральне", "Індивідуальне"]}
                  onInputChange={(e, newValue) => setHeating(newValue)}
                  onKeyPress={noEnter}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      type="text"
                      variant="outlined"
                      label="Опалення *"
                      {...required(heating)}
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Stack direction="row" spacing={3} sx={{ mt: 0.5 }}>
              <FormControlLabel
                control={<Checkbox checked={furniture} />}
                label="Меблі"
                onChange={(e) => setFurniture(e.currentTarget.checked)}
              />
              <FormControlLabel
                control={<Checkbox checked={technology} />}
                label="Техніка"
                onChange={(e) => setTechnology(e.currentTarget.checked)}
              />
            </Stack>
          </FormSection>

          <FormSection
            title="Власник"
            icon={<PersonOutlined fontSize="small" />}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="add-name"
                  label="Ім'я власника"
                  variant="outlined"
                  type="text"
                  value={personName}
                  onKeyPress={noEnter}
                  onChange={(e) => setPersonName(e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="add-phone"
                  label="Номер телефону"
                  variant="outlined"
                  type="text"
                  value={phoneNumber}
                  onKeyPress={noEnter}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  fullWidth
                />
              </Grid>
            </Grid>
            <Typography variant="caption" sx={{ color: tokens.ink400 }}>
              Не показується на публічному сайті.
            </Typography>
          </FormSection>

          <FormSection title="Опис" icon={<NotesOutlined fontSize="small" />}>
            <TextField
              id="add-info"
              placeholder="Ставте посилання в [ ]"
              label="Додаткова інформація"
              variant="outlined"
              type="text"
              value={info}
              onChange={(e) => setInfo(e.target.value)}
              fullWidth
              multiline
              minRows={3}
            />
          </FormSection>

          <FormSection
            title="Фото"
            icon={<PhotoLibraryOutlined fontSize="small" />}
            sx={{ mb: 1 }}
          >
            <HiddenInput
              multiple
              accept="image/*"
              id="contained-button-file"
              type="file"
              ref={inputFile}
              onChange={handleFileInput}
            />
            <Button
              variant="outlined"
              startIcon={<CloudUploadOutlined />}
              onClick={() => inputFile.current && inputFile.current.click()}
            >
              Вибрати файли
            </Button>

            <SelectedPhotos
              files={inputFiles}
              onRemove={removeFile}
              onReorder={setInputFiles}
            />
          </FormSection>

          {showErrors && missing.length > 0 && (
            <Alert severity="error" sx={{ mt: 1 }}>
              Заповніть обов'язкові поля, позначені зірочкою.
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{ px: 3, py: 2, borderTop: "1px solid " + tokens.line, gap: 1 }}
      >
        <Button variant="text" onClick={onCloseDialog}>
          Скасувати
        </Button>
        <Button
          variant="contained"
          disableElevation
          startIcon={<AddRounded />}
          onClick={trySave}
        >
          Додати оголошення
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddRealEstateObject;
